import React from "react";
import { Box, Container, Grid, Typography, Link, IconButton } from "@mui/material";
import InstagramIcon from '@mui/icons-material/Instagram';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function Footer() {
  const restaurants = [
    { name: "Korean BBQ Edinburgh", link: "https://koreanbbq.kjgroups.co.uk" },
    { name: "Korean BBQ Dundee", link: "https://koreanbbqdundee.kjgroups.co.uk" },
    { name: "Korean Munchies", link: "https://koreanmunchies.kjgroups.co.uk" },
    { name: "Soul Sushi", link: "https://soulsushi.kjgroups.co.uk" },
  ];

  const quickLinks = [
    { name: "About Us", link: "#about" },
    { name: "Our Story", link: "#story" },
    { name: "Careers", link: "#careers" },
    { name: "Press", link: "#press" },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, rgba(10, 10, 10, 0.98), rgba(20, 20, 20, 0.98))',
        color: "#fff",
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, transparent, rgba(255,77,77,0.5), rgba(249,203,40,0.5), transparent)'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-50%',
          right: '-10%',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255, 77, 77, 0.05), transparent 70%)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }
      }}
    >
      {/* Decorative Grid Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(90deg, rgba(255,77,77,0.02) 1px, transparent 1px),
            linear-gradient(rgba(255,77,77,0.02) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          opacity: 0.3,
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Main Footer Content */}
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={{ xs: 4, md: 6 }}>
            {/* Logo and Description */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(255, 77, 77, 0.3)',
                  }}
                >
                  <RestaurantIcon sx={{ fontSize: '22px', color: '#fff' }} />
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    letterSpacing: '1px'
                  }}
                >
                  KJ GROUP
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  mb: 3,
                  lineHeight: 1.7,
                  fontSize: '0.9rem'
                }}
              >
                Bringing authentic Asian cuisine to Scotland with passion, quality, and innovation across our diverse collection of restaurants.
              </Typography>

              {/* Social Media */}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                <IconButton
                  component="a"
                  href="https://www.instagram.com/kjgroup.uk"
                  target="_blank"
                  sx={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    color: '#fff',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #833AB4, #E1306C)',
                      borderColor: 'transparent',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(225, 48, 108, 0.4)',
                    }
                  }}
                >
                  <InstagramIcon sx={{ fontSize: '20px' }} />
                </IconButton>
              </Box>
            </Grid>

            {/* Our Restaurants */}
            <Grid item xs={12} sm={6} md={3}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#fff',
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '40px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #FF4D4D, #F9CB28)',
                    borderRadius: '2px',
                  }
                }}
              >
                Our Locations
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 4 }}>
                {restaurants.map((restaurant, index) => (
                  <Link
                    key={index}
                    href={restaurant.link}
                    target="_blank"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'rgba(255,255,255,0.6)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      paddingLeft: '0',
                      '&:hover': {
                        color: '#FF4D4D',
                        paddingLeft: '8px',
                        '& .arrow-icon': {
                          opacity: 1,
                          transform: 'translateX(0)',
                        }
                      }
                    }}
                  >
                    <ArrowForwardIcon
                      className="arrow-icon"
                      sx={{
                        fontSize: '16px',
                        opacity: 0,
                        transform: 'translateX(-8px)',
                        transition: 'all 0.3s ease',
                      }}
                    />
                    {restaurant.name}
                  </Link>
                ))}
              </Box>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={12} sm={6} md={2}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#fff',
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '40px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #FF4D4D, #F9CB28)',
                    borderRadius: '2px',
                  }
                }}
              >
                Quick Links
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 4 }}>
                {quickLinks.map((link, index) => (
                  <Link
                    key={index}
                    href={link.link}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'rgba(255,255,255,0.6)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      paddingLeft: '0',
                      '&:hover': {
                        color: '#F9CB28',
                        paddingLeft: '8px',
                        '& .arrow-icon': {
                          opacity: 1,
                          transform: 'translateX(0)',
                        }
                      }
                    }}
                  >
                    <ArrowForwardIcon
                      className="arrow-icon"
                      sx={{
                        fontSize: '16px',
                        opacity: 0,
                        transform: 'translateX(-8px)',
                        transition: 'all 0.3s ease',
                      }}
                    />
                    {link.name}
                  </Link>
                ))}
              </Box>
            </Grid>

            {/* Contact Information */}
            <Grid item xs={12} md={3}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#fff',
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-8px',
                    left: 0,
                    width: '40px',
                    height: '3px',
                    background: 'linear-gradient(90deg, #FF4D4D, #F9CB28)',
                    borderRadius: '2px',
                  }
                }}
              >
                Get In Touch
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 4 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    padding: '12px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(255, 77, 77, 0.08)',
                      borderColor: 'rgba(255, 77, 77, 0.2)',
                    }
                  }}
                >
                  <LocationOnIcon sx={{ color: '#FF4D4D', fontSize: '20px', mt: 0.3 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', lineHeight: 1.6 }}>
                    Edinburgh & Dundee<br />Scotland, UK
                  </Typography>
                </Box>

                <Link
                  href="tel:+441313576000"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    padding: '12px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(255, 77, 77, 0.08)',
                      borderColor: 'rgba(255, 77, 77, 0.2)',
                      color: '#FF4D4D',
                    }
                  }}
                >
                  <PhoneIcon sx={{ color: '#FF4D4D', fontSize: '20px' }} />
                  +44 131 357 6000
                </Link>

                <Link
                  href="mailto:contact@kjgroups.co.uk"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    padding: '12px',
                    borderRadius: '10px',
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    color: 'rgba(255,255,255,0.7)',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(249, 203, 40, 0.08)',
                      borderColor: 'rgba(249, 203, 40, 0.2)',
                      color: '#F9CB28',
                    }
                  }}
                >
                  <EmailIcon sx={{ color: '#F9CB28', fontSize: '20px' }} />
                  contact@kjgroups.co.uk
                </Link>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Bottom Bar */}
        <Box
          sx={{
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            py: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.875rem',
            }}
          >
            © {new Date().getFullYear()} KJ Group. All rights reserved.
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.875rem',
            }}
          >
            Crafted with precision by{' '}
            <Link
              href="https://devclubm.com"
              target="_blank"
              sx={{
                color: '#FF4D4D',
                textDecoration: 'none',
                fontWeight: 600,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF4D4D, #F9CB28)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }
              }}
            >
              DevClub
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default Footer;
